//Stores
import { vialInfoStore } from 'src/stores/features/cury/vial-info';
//Types
import type { VialColors } from 'src/types/Cury';

/**
 * Fetches and processes vial color information for both left and right slots.
 *
 * This function retrieves vial color data from the device and converts RGB values
 * to CSS color strings. It handles both slots (left and right) and provides fallback
 * colors if vial information is unavailable or invalid. The function ensures that
 * color data is fetched and cached through the vial info store.
 *
 * @param {any} device - The Cury device object containing device information and connection details
 *
 * @returns {Promise<VialColors>} A promise that resolves to an object containing:
 *   - `left`: {string} CSS color string for the left vial (RGB format or CSS variable)
 *   - `right`: {string} CSS color string for the right vial (RGB format or CSS variable)
 *
 * @example
 * // Fetch vial colors for display
 * const vialColors = await fetchVialColor(curyDevice);
 * console.log('Left vial color:', vialColors.left); // 'rgb(255, 0, 0)' or 'var(--color-muted)'
 * console.log('Right vial color:', vialColors.right); // 'rgb(0, 255, 0)' or 'var(--color-muted)'
 *
 * @example
 * // Use colors in UI components
 * const colors = await fetchVialColor(device);
 * document.getElementById('left-vial').style.backgroundColor = colors.left;
 * document.getElementById('right-vial').style.backgroundColor = colors.right;
 *
 * @example
 * // Handle fallback colors
 * const colors = await fetchVialColor(device);
 * if (colors.left === 'var(--color-muted)') {
 *   console.log('Left vial color not available, using default');
 * }
 *
 */
export const fetchVialColor = async (device: any): Promise<VialColors> => {
	const defaultColor = 'var(--color-muted)';
	const colors: VialColors = { left: defaultColor, right: defaultColor };

	try {
		const info = await vialInfoStore.ensureFetched(device);
		['left', 'right'].forEach((slot) => {
			const rgb = info?.[slot]?.colorRgb as [number, number, number] | undefined;
			if (!rgb) {
				colors[slot as 'left' | 'right'] = defaultColor;
				return;
			}
			colors[slot as 'left' | 'right'] = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
		});
	} catch (err) {
		console.log('Failed to fetch vial colors:', err);
	}

	return colors;
};
